import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaTasks, FaCheckCircle, FaClock } from "react-icons/fa";
import { FiPlusCircle, FiEdit, FiTrash2, FiLoader, FiUser } from "react-icons/fi";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from "react-toastify";
import Select from "react-select";

// API
import { getActivities } from "../../api/activityApi";
import { getStaff } from "../../api/staff-calls-api";
import { deleteTask, getTasks, updateTask } from "../../api/taskapi";

// Components
import SidebarNav from '../../components/MasterSideBar';
import TopbarNav from "../../components/TopbarNav";
import AddTaskModal from "./AddTaskModal";

// --- Sub-component: Stat Card ---
const StatCard = ({ title, value, icon, loading, color }) => {
    const colorMap = {
        orange: "text-orange-600 bg-orange-50",
        blue: "text-blue-600 bg-blue-50",
        green: "text-green-600 bg-green-50"
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between transition-all duration-200 hover:shadow-md">
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{title}</p>
                {loading ? (
                    <div className="mt-2 h-8 w-16 bg-gray-100 animate-pulse rounded" />
                ) : (
                    <p className="text-3xl font-black text-gray-800 mt-1">{value}</p>
                )}
            </div>
            <div className={`p-4 rounded-xl ${colorMap[color]}`}>
                {React.cloneElement(icon, { size: 24 })}
            </div>
        </div>
    );
};

// --- Sub-component: Task Card ---
const TaskCard = ({ task, onEdit, onDelete, deleting }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case "in_progress": return "border-blue-500";
            case "pending": return "border-yellow-500";
            case "completed": return "border-green-500";
            case "planned": return "border-purple-500";
            case "cancelled": return "border-red-500";
            default: return "border-gray-400";
        }
    };

    const getPriorityBadge = (priority) => {
        const colors = {
            high: "bg-red-50 text-red-600 border-red-100",
            medium: "bg-orange-50 text-orange-600 border-orange-100",
            low: "bg-green-50 text-green-600 border-green-100"
        };
        return colors[priority] || "bg-gray-50 text-gray-600 border-gray-100";
    };

    return (
        <div className={`bg-white p-4 rounded-xl shadow-sm border-l-4 mb-3 transition-all group hover:shadow-md ${getStatusColor(task.status)} ${deleting ? 'opacity-50 grayscale' : ''}`}>
            <div className="flex justify-between items-start gap-2">
                <h4 className="font-bold text-gray-800 text-xs leading-tight group-hover:text-orange-600 transition-colors">{task.title}</h4>
                <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEdit(task)} className="p-1 text-gray-400 hover:text-blue-600"><FiEdit size={14} /></button>
                    <button onClick={() => onDelete(task._id)} className="p-1 text-gray-400 hover:text-red-600"><FiTrash2 size={14} /></button>
                </div>
            </div>

            <div className="mt-3 space-y-2">
                <div className="flex items-center text-[10px] text-gray-500 font-bold uppercase tracking-tighter">
                    <FiUser className="mr-1 text-orange-400" size={12} />
                    <span className="truncate">{task.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}` : "Unassigned"}</span>
                </div>
                
                <div className="flex justify-between items-center">
                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${getPriorityBadge(task.priority)}`}>
                        {task.priority || 'Normal'}
                    </span>
                    <span className="text-[10px] font-black text-gray-400">{task.progress || 0}%</span>
                </div>

                <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                    <div className="bg-orange-500 h-full transition-all duration-500" style={{ width: `${task.progress || 0}%` }} />
                </div>
            </div>
        </div>
    );
};

// --- Sub-component: Kanban Column ---
const KanbanColumn = ({ status, tasks, onEdit, onDelete, deletingTaskId }) => {
    const titles = {
        planned: "Planned",
        pending: "Backlog",
        in_progress: "Active",
        completed: "Done",
        cancelled: "Void",
    };

    return (
        <Droppable droppableId={status}>
            {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="bg-gray-100/50 rounded-2xl p-3 flex-shrink-0 w-72 h-[calc(100vh-400px)] flex flex-col border border-gray-200/50">
                    <div className="flex items-center justify-between mb-4 px-2">
                        <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{titles[status]}</h3>
                        <span className="bg-white px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-400 border border-gray-200">{tasks.length}</span>
                    </div>
                    <div className="overflow-y-auto flex-grow pr-1 custom-scrollbar">
                        {tasks.map((task, index) => (
                            <Draggable key={task._id} draggableId={task._id} index={index}>
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                        <TaskCard task={task} onEdit={onEdit} onDelete={onDelete} deleting={deletingTaskId === task._id} />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                </div>
            )}
        </Droppable>
    );
};

// --- Main Page ---
const TaskPage = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [activities, setActivities] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedActivityId, setSelectedActivityId] = useState("");
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [deletingTaskId, setDeletingTaskId] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const fetchTasksAndActivities = async () => {
        setLoading(true);
        try {
            const [fetchedTasks, fetchedActivities, staffResponse] = await Promise.all([
                getTasks(), getActivities(), getStaff()
            ]);
            setTasks(fetchedTasks);
            setActivities(Array.isArray(fetchedActivities) ? fetchedActivities : fetchedActivities.activities || []);
            if (staffResponse?.list) {
                setStaffOptions(staffResponse.list.map(s => ({ value: s._id, label: `${s.firstName} ${s.lastName}` })));
            }
        } catch (err) {
            toast.error("Data sync failed.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTasksAndActivities(); }, []);

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

        const newStatus = destination.droppableId;
        const originalTasks = [...tasks];
        setTasks(tasks.map(t => t._id === draggableId ? { ...t, status: newStatus } : t));
        setUpdatingStatus(true);

        try {
            await updateTask(draggableId, { status: newStatus });
            toast.success("Status Updated");
        } catch (error) {
            setTasks(originalTasks);
            toast.error("Sync Failed");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesActivity = !selectedActivityId || (task.activity?._id === selectedActivityId);
            const matchesAssignee = !selectedAssignee || (task.assignedTo?._id === selectedAssignee.value);
            const matchesPriority = !selectedPriority || task.priority === selectedPriority.value;
            return matchesSearch && matchesActivity && matchesAssignee && matchesPriority;
        });
    }, [tasks, searchQuery, selectedActivityId, selectedAssignee, selectedPriority]);

    const tasksByStatus = useMemo(() => ({
        planned: filteredTasks.filter(t => t.status === "planned"),
        pending: filteredTasks.filter(t => t.status === "pending"),
        in_progress: filteredTasks.filter(t => t.status === "in_progress"),
        completed: filteredTasks.filter(t => t.status === "completed"),
        cancelled: filteredTasks.filter(t => t.status === "cancelled"),
    }), [filteredTasks]);

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <SidebarNav 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); navigate("/"); }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchQuery} />

                <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto overflow-x-hidden">
                    <div className="max-w-[1600px] mx-auto space-y-6">
                        {/* Header */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Task Management</h1>
                                <p className="text-gray-500 text-sm">Visualize project workflow and bottlenecks.</p>
                            </div>
                            <button
                                onClick={() => { setSelectedTask(null); setIsModalOpen(true); }}
                                disabled={!selectedActivityId}
                                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold uppercase text-xs tracking-widest transition-all shadow-lg ${!selectedActivityId ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' : 'bg-orange-600 text-white hover:bg-orange-700 shadow-orange-100'}`}
                            >
                                <FiPlusCircle size={18} /> Add Task
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <StatCard title="Total Scope" value={filteredTasks.length} icon={<FaTasks />} loading={loading} color="orange" />
                            <StatCard title="Active" value={tasksByStatus.in_progress.length} icon={<FaClock />} loading={loading} color="blue" />
                            <StatCard title="Closed" value={tasksByStatus.completed.length} icon={<FaCheckCircle />} loading={loading} color="green" />
                        </div>

                        {/* Filters Container */}
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Activity Context</label>
                                    <select 
                                        className="w-full bg-gray-50 border-none rounded-xl px-4 py-2 text-sm font-bold text-gray-700 focus:ring-2 focus:ring-orange-500/20"
                                        value={selectedActivityId}
                                        onChange={(e) => setSelectedActivityId(e.target.value)}
                                    >
                                        <option value="">Select Project Activity...</option>
                                        {activities.map(a => <option key={a._id} value={a._id}>{a.activityTitle}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Team Member</label>
                                    <Select isClearable options={staffOptions} value={selectedAssignee} onChange={setSelectedAssignee} placeholder="Filter Assignee..." className="text-sm font-bold" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest">Priority</label>
                                    <Select isClearable options={[{value:"high", label:"High"}, {value:"medium", label:"Medium"}, {value:"low", label:"Low"}]} value={selectedPriority} onChange={setSelectedPriority} placeholder="All Priorities" className="text-sm font-bold" />
                                </div>
                            </div>

                            {/* Kanban Board with Horizontal Scroll */}
                            {loading ? (
                                <div className="py-24 flex flex-col items-center justify-center">
                                    <FiLoader className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Initializing Board...</p>
                                </div>
                            ) : (
                                <div className="relative">
                                    {updatingStatus && (
                                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20">
                                            <div className="bg-orange-600 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase flex items-center gap-2 shadow-xl animate-pulse">
                                                <FiLoader className="animate-spin" /> Updating Status
                                            </div>
                                        </div>
                                    )}
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar min-h-[450px]">
                                            {['planned', 'pending', 'in_progress', 'completed', 'cancelled'].map(status => (
                                                <KanbanColumn 
                                                    key={status} 
                                                    status={status} 
                                                    tasks={tasksByStatus[status]} 
                                                    onEdit={(task) => { setSelectedTask(task); setIsModalOpen(true); }} 
                                                    onDelete={async (id) => { if(window.confirm("Permanent Delete?")) { await deleteTask(id); fetchTasksAndActivities(); } }}
                                                    deletingTaskId={deletingTaskId} 
                                                />
                                            ))}
                                        </div>
                                    </DragDropContext>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
            
            <AddTaskModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelectedTask(null); fetchTasksAndActivities(); }}
                activityId={selectedActivityId}
                onTaskCreated={fetchTasksAndActivities}
                task={selectedTask}
            />
        </div>
    );
};

export default TaskPage;